// Layout Constants
export const LAYOUT = {
  headerHeight: "56px",  // Reduced from 64px
  sidebarWidth: "320px", // Reduced from 380px for better screen real estate
  footerHeight: "48px",
  maxContentWidth: "1440px",
};

// Grid System
export const GRID = {
  containerWidth: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  gutterWidth: "1.5rem", // Reduced from 2rem for tighter spacing
  columns: 12,
};

// Spacing Scale (matches Tailwind's default scale but can be customized)
export const SPACING = {
  px: "1px",
  0: "0",
  0.5: "0.125rem", // 2px
  1: "0.25rem",    // 4px
  1.5: "0.375rem", // 6px
  2: "0.5rem",     // 8px
  2.5: "0.625rem", // 10px
  3: "0.75rem",    // 12px
  3.5: "0.875rem", // 14px
  4: "1rem",       // 16px
  5: "1.25rem",    // 20px
  6: "1.5rem",     // 24px
  7: "1.75rem",    // 28px
  8: "2rem",       // 32px
  9: "2.25rem",    // 36px
  10: "2.5rem",    // 40px
  11: "2.75rem",   // 44px
  12: "3rem",      // 48px
  14: "3.5rem",    // 56px
  16: "4rem",      // 64px
  20: "5rem",      // 80px
  24: "6rem",      // 96px
  28: "7rem",      // 112px
  32: "8rem",      // 128px
  36: "9rem",      // 144px
  40: "10rem",     // 160px
  44: "11rem",     // 176px
  48: "12rem",     // 192px
  52: "13rem",     // 208px
  56: "14rem",     // 224px
  60: "14rem",     // 240px
  64: "16rem",     // 256px
  72: "18rem",     // 288px
  80: "20rem",     // 320px
  96: "24rem",     // 384px
};

// Z-index scale
export const Z_INDEX = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

// Common border radius values
export const RADII = {
  none: "0",
  sm: "0.125rem",    // 2px
  base: "0.25rem",   // 4px
  md: "0.375rem",    // 6px
  lg: "0.5rem",      // 8px
  xl: "0.75rem",     // 12px
  "2xl": "1rem",     // 16px
  "3xl": "1.5rem",   // 24px
  full: "9999px",
};

// Input sizes
export const INPUT_SIZES = {
  sm: {
    height: "32px",
    fontSize: "0.875rem",
    padding: "0.5rem 0.75rem",
  },
  md: {
    height: "40px",
    fontSize: "1rem",
    padding: "0.625rem 1rem",
  },
  lg: {
    height: "48px",
    fontSize: "1.125rem",
    padding: "0.75rem 1.25rem",
  },
};

// Border radius
export const BORDER_RADIUS = {
  none: "0",
  sm: "0.125rem",
  base: "0.25rem",
  md: "0.375rem",
  lg: "0.5rem",
  xl: "0.75rem",
  "2xl": "1rem",
  "3xl": "1.5rem",
  full: "9999px",
};

// Transition presets
export const TRANSITIONS = {
  fast: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
  normal: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
};

// Chart size presets - Added responsive sizes
export const CHART_SIZES = {
  sm: {
    height: 180,
    width: 180,
    iconSize: 16,
    fontSize: "text-xs",
    padding: "p-2",
  },
  md: {
    height: 220,
    width: 220,
    iconSize: 20,
    fontSize: "text-sm",
    padding: "p-3",
  },
  lg: {
    height: 260,
    width: 260,
    iconSize: 24,
    fontSize: "text-base",
    padding: "p-4",
  }
};

// Card patterns - Updated with responsive classes
export const CARD_BASE = "bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-none p-2 md:p-4 xl:p-6 text-xs md:text-sm xl:text-base";
export const CARD_VARIANTS = {
  dense: "bg-white dark:bg-gray-800 rounded-md shadow-sm dark:shadow-none p-2 text-xs",
  compact: "bg-white dark:bg-gray-800 rounded-md shadow dark:shadow-none p-3 md:p-4 text-sm",
  spacious: "bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-none p-4 md:p-6 xl:p-8 text-sm md:text-base",
};

// Table patterns - Updated with responsive classes
export const TABLE_BASE = "table-auto w-full text-xs md:text-sm xl:text-base";
export const TABLE_CELL_BASE = "px-2 py-1";
export const TABLE_VARIANTS = {
  header: "font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 text-xs md:text-sm",
  cell: "text-gray-900 dark:text-gray-100",
  row: "border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors",
  dense: {
    header: "px-1.5 py-1 text-xs font-medium",
    cell: "px-1.5 py-1 text-xs",
  },
  default: {
    header: "px-2 py-1.5 text-xs md:text-sm font-medium",
    cell: "px-2 py-1.5 text-xs md:text-sm",
  },
  spacious: {
    header: "px-3 py-2 text-sm md:text-base font-medium",
    cell: "px-3 py-2 text-sm md:text-base",
  }
};

// Container patterns
export const CONTAINER_BASE = "mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl";
export const CONTAINER_VARIANTS = {
  sm: "max-w-3xl",
  md: "max-w-5xl",
  lg: "max-w-7xl",
  xl: "max-w-[90rem]",
  full: "max-w-none",
};

// Section patterns
export const SECTION_BASE = "py-4 md:py-6 xl:py-8";
export const SECTION_VARIANTS = {
  sm: "py-2 md:py-3 xl:py-4",
  md: "py-4 md:py-6 xl:py-8",
  lg: "py-6 md:py-8 xl:py-12",
  xl: "py-8 md:py-12 xl:py-16",
};
